import { useEffect, useRef } from "react";

import {
  GetExperiencesResponseBodyType,
  GetExperiencesType,
} from "@intelligems/ig-types";
import { api } from "@src/api/api";
import { getNextPageParam } from "@src/api/getNextPageParam";
import { useInfiniteQuery } from "@tanstack/react-query";

const getExperiences = async (options: {
  type: GetExperiencesType;
  page?: number;
  limit?: number;
}) => {
  const resp = await api.get<GetExperiencesResponseBodyType>(
    `/v2/experiences`,
    {
      params: {
        page: options.page,
        limit: options.limit,
        type: options.type,
      },
    }
  );
  return normalizeExperiencesPage(resp.data);
};

/**
 * Normalize a page before it enters the react-query cache: a malformed/empty
 * backend payload can arrive without the `experiences` array, and cache
 * updaters (updateInExperiencesCache & co in queryKeys.ts) map/filter/spread
 * `page.experiences` unconditionally on later mutations.
 */
export const normalizeExperiencesPage = (
  page: GetExperiencesResponseBodyType
): GetExperiencesResponseBodyType => ({
  ...page,
  experiences: page.experiences ?? [],
});

export const getExperiencesNextPageParam = (
  lastPage: GetExperiencesResponseBodyType,
  limit: number
): number | undefined =>
  getNextPageParam(lastPage.experiences, lastPage.page, limit);

export const useGetExperiences = (type: GetExperiencesType) => {
  const limit = 50;
  const fetchingRef = useRef(false);

  const experiences = useInfiniteQuery<
    GetExperiencesResponseBodyType,
    Error,
    GetExperiencesResponseBodyType
  >({
    initialPageParam: 1,
    queryKey: [{ scope: `experiences-${type}` }],
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) =>
      getExperiences({ type, page: pageParam as number, limit }),
    getNextPageParam: (lastPage) =>
      getExperiencesNextPageParam(lastPage, limit),
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page && firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    select: (data) => {
      return {
        pages: data.pages,
        pageParams: data.pageParams,
        experiences: data.pages.flatMap((page) => page.experiences ?? []),
      };
    },
  });

  useEffect(() => {
    if (
      experiences.hasNextPage &&
      !experiences.isFetchingNextPage &&
      !fetchingRef.current
    ) {
      fetchingRef.current = true;
      void experiences.fetchNextPage().finally(() => {
        fetchingRef.current = false;
      });
    }
  }, [experiences.hasNextPage, experiences.isFetchingNextPage]);

  return experiences;
};
